import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import { Button } from '~components/generic/editor/Button/Button';
import { ButtonGroup } from '~components/generic/editor/ButtonGroup/ButtonGroup';
import AddLink from '~components/generic/elements/add-link/AddLink';
import { ModuleAttachments } from '~components/generic/module-attachments/ModuleAttachments';
import { ArrowDownIcon, ArrowUpIcon, DeleteIcon } from '~components/icons';
import useLabels from '~hooks/useLabels';
import { IEntityComponentProps } from '~interfaces/props';
import { moveItemInsideArray, removeItemFromArrayAtPosition } from '~services/immutable';

export default function ModuleChecklist({ data, editing, className, onChange, origData }: IEntityComponentProps) {
  const {
    labelModuleChecklistNewItem,
    msgMoveUpChecklistItemHint,
    msgMoveDownChecklistItemHint,
    msgDeleteChecklistItemHint,
  } = useLabels([
    'labelModuleChecklistNewItem',
    'msgMoveUpChecklistItemHint',
    'msgMoveDownChecklistItemHint',
    'msgDeleteChecklistItemHint',
  ]);
  const ITEMS_FIELD_NAME = 'attributes.field_checklist_items';

  const handleItemAdd = () => onChange(ITEMS_FIELD_NAME, labelModuleChecklistNewItem, true);
  const { field_checklist_items, field_hide_title, field_title, field_text, field_hide_text } = data.attributes;
  const checklistItems = Array.isArray(field_checklist_items) ? field_checklist_items : [];
  const countChecklistItems = checklistItems.length;

  const [itemKeys, setItemKeys] = useState<string[]>(() => checklistItems.map(() => crypto.randomUUID()));
  const [updateRequired, setUpdateRequired] = useState<boolean>(false);

  const regenerateKeys = () => {
    setItemKeys(() => checklistItems.map(() => crypto.randomUUID()));
  };

  useEffect(() => {
    if (updateRequired) {
      setUpdateRequired(false);
      regenerateKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setUpdateRequired(true);
  }, [checklistItems.length]);

  const onMoveUp = useCallback(
    (index: number) => () => {
      const items: unknown[] = field_checklist_items || [];
      if (index <= 0 || index >= items.length) {
        return;
      }
      onChange(ITEMS_FIELD_NAME, moveItemInsideArray(items, index, index - 1));
      setUpdateRequired(true);
    },
    [field_checklist_items, onChange],
  );
  const onMoveDown = useCallback(
    (index: number) => () => {
      const items: unknown[] = field_checklist_items || [];
      if (index + 1 >= items.length) {
        return;
      }
      onChange(ITEMS_FIELD_NAME, moveItemInsideArray(items, index, index + 1));
      setUpdateRequired(true);
    },
    [field_checklist_items, onChange],
  );

  const handleItemDelete = useCallback(
    (index: number) => () => {
      const items: unknown[] = field_checklist_items || [];
      onChange(ITEMS_FIELD_NAME, removeItemFromArrayAtPosition(items, index));
      setUpdateRequired(true);
    },
    [field_checklist_items, onChange],
  );

  if (!data) {
    return null;
  }

  const clsModule = clsx({ ...className.module }, 'relative');
  const clsContainer = clsx({ ...className.container }, 'px-0 relative');
  const clsHeader = clsx('row c-module__header');
  const clsHeaderContent = clsx('col col-12');
  const clsContent = clsx('row c-module__content');
  const clsText = clsx('col col-12 u-margin-top-sm u-margin-bottom-lg o-field-text');
  const clsList = clsx('col col-12 u-margin-top-md');

  return (
    <div className={clsModule} id={origData}>
      <div className={clsContainer}>
        <div className={clsHeader}>
          <div className={clsHeaderContent}>
            <EditableField
              hide={field_hide_title}
              entityType={data.type}
              fieldName="attributes.field_title.value"
              data={field_title.value}
              editing={editing}
              onChange={onChange}
            />
          </div>
          <ModuleAttachments data={data} />
        </div>
        <div className={clsContent}>
          {!!field_text && (
            <div className={clsText}>
              <EditableField
                hide={field_hide_text}
                entityType={data.type}
                fieldName="attributes.field_text.value"
                data={field_text.value}
                editing={editing}
                onChange={onChange}
              />
            </div>
          )}
          <div className={clsList}>
            <ul className="c-checklist">
              {checklistItems.map((item, index) => (
                <li key={itemKeys[index]} className="c-checklist__item relative">
                  <div className="u-inline-block c-checklist__icon-col u-vertical-top">
                    <i className="c-icon-custom c-icon-custom--vertical-centered c-icon-custom--checkmark-checked u-margin-right-md u-size-lg u-line-height-1" />
                  </div>
                  <div className="u-inline-block c-checklist__content-col">
                    <EditableField
                      entityType={data.type}
                      index={index}
                      fieldName={ITEMS_FIELD_NAME}
                      data={item}
                      editing={editing}
                      onChange={onChange}
                    />
                  </div>
                  {editing && (
                    <div className="u-absolute u-absolute--right">
                      <ButtonGroup>
                        <Button
                          disabled={index === 0}
                          title={msgMoveUpChecklistItemHint}
                          onClick={onMoveUp(index)}
                          icon={<ArrowUpIcon />}
                        />
                        <Button
                          disabled={index + 1 === countChecklistItems}
                          title={msgMoveDownChecklistItemHint}
                          onClick={onMoveDown(index)}
                          icon={<ArrowDownIcon />}
                        />
                        <Button
                          title={msgDeleteChecklistItemHint}
                          onClick={handleItemDelete(index)}
                          icon={<DeleteIcon />}
                        />
                      </ButtonGroup>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {editing && <AddLink className={{ 'u-block': true, 'u-margin-top-md': true }} onClick={handleItemAdd} />}
          </div>
        </div>
      </div>
    </div>
  );
}
